/**
 * The brand's storage adapter: Firestore plus `/app`'s local keys.
 *
 * Kept apart from `lib/brand.js` so the decisions there stay pure and only
 * this file knows about Firebase — the same split as `theme.js` /
 * `theme-store.js`.
 *
 * ── This is the wiring that was missing ───────────────────────────────────
 *
 * `brand.js` has been 231 finished, tested lines since it was written, and
 * NOTHING imported it. `applyBrand` was never called, so every button, focus
 * ring and active tab in `/v2` used the default lavender no matter what an
 * advisor had chosen, and the six heading and six body faces the Account
 * screen offers were unreachable. The tokens were plumbed
 * (`styles/tokens.css` layer 3 maps `--brand-primary` to `--accent` and
 * `--brand-font-*` to the two families), the settings keys were synced by
 * `/app`, and the one line joining them did not exist.
 *
 * ── Two phases, for the same reason the theme has two ─────────────────────
 *
 * Reading the settings document is a network round trip. Waiting for it before
 * painting means an advisor watches their own colours arrive a beat late on
 * every load, which reads worse than not having them. So `/app`'s local copy
 * is applied synchronously the moment the uid is known, and the cloud
 * reconciles afterwards. Both programs write the local copy, so in practice
 * phase two changes nothing.
 */

import { readDoc, mergeDoc } from './firebase.js'
import { unwrap, wrap } from './settings.js'
import { BRAND_KEYS, applyBrand, readBrand, writeBrand } from './brand.js'
import { setAgencyName, setAgencyPhone, setLogo, setLogoOnSocial } from './agency.js'

/**
 * `/app` keeps these in localStorage under a per-uid namespace, the same way
 * it keeps the theme. Read directly rather than through `lib/store.js` so this
 * works before `setNamespace` has run.
 *
 * @param {string} uid @param {string} key
 * @returns {string}
 */
function localValue(uid, key) {
  try {
    const raw = globalThis.localStorage
      ? globalThis.localStorage.getItem(`${uid}::${key}`) ?? globalThis.localStorage.getItem(key)
      : null
    return raw == null ? '' : String(raw)
  } catch {
    /* Safari in private mode throws on localStorage. Not having a cached
       brand is a slower first paint, not a failure. */
    return ''
  }
}

/** @param {string} uid @returns {Record<string, string>} */
function localBrandValues(uid) {
  /** @type {Record<string, string>} */
  const out = {}
  for (const key of Object.values(BRAND_KEYS)) {
    const value = localValue(uid, key)
    if (value) out[key] = value
  }
  return out
}

/** @param {string} uid @param {Record<string, string>} values */
function writeLocalBrand(uid, values) {
  try {
    if (!globalThis.localStorage) return
    for (const key of Object.values(BRAND_KEYS)) {
      const value = values[key]
      if (value) globalThis.localStorage.setItem(`${uid}::${key}`, value)
      else globalThis.localStorage.removeItem(`${uid}::${key}`)
    }
  } catch {
    /* See above. The cloud copy is the one that matters. */
  }
}

/** The element the brand is written to. `:root`, so `--accent` resolves. */
function rootElement() {
  return globalThis.document ? globalThis.document.documentElement : null
}

/**
 * Phase one: paint the brand we can know without waiting.
 *
 * @param {string} uid
 * @returns {import('./brand.js').Brand}
 */
export function applyCachedBrand(uid) {
  const brand = readBrand(localBrandValues(uid))
  paint(brand)
  return brand
}

/** The last brand painted, so a theme change can re-run it. */
let applied = /** @type {import('./brand.js').Brand|null} */ (null)

/**
 * Re-run the last brand against the theme that is on the page NOW.
 *
 * `applyBrand` suppresses the three colour properties in Slate, so switching
 * theme has to re-decide: coming back to Grand must put the advisor's colours
 * back, and going to Slate must take them off. Called by `theme-store.js` the
 * moment the class changes.
 */
export function reapplyBrand() {
  if (applied) paint(applied)
}

/**
 * Put a brand on the page.
 *
 * `applyBrand` handles the colours and fonts, which are CSS custom properties.
 * The agency NAME is not one — it is text inside the wordmark — so it goes to
 * `lib/agency.js`, which the views that draw a wordmark read at render time.
 * Both halves in one function, so no caller can do one and forget the other.
 *
 * @param {import('./brand.js').Brand} brand
 */
function paint(brand) {
  applied = brand
  applyBrand(rootElement(), brand)
  setAgencyName(brand.agencyName)
  setAgencyPhone(brand.agencyPhone)
  setLogo(brand.logo)
  setLogoOnSocial(brand.logoOnSocial)
}

/**
 * Phase two: reconcile against the shared settings document.
 *
 * A failed read leaves the cached brand alone rather than snapping to the
 * default lavender. An advisor's colours vanishing on their own because a
 * network call failed is worse than being one round trip out of date — the
 * same trade `syncThemeFromCloud` makes.
 *
 * @param {string} uid
 * @returns {Promise<import('./brand.js').Brand>}
 */
export async function syncBrandFromCloud(uid) {
  const cached = readBrand(localBrandValues(uid))
  try {
    const settings = await readDoc(['users', uid, 'settings', 'main'])
    /** @type {Record<string, any>} */
    const values = {}
    for (const key of Object.values(BRAND_KEYS)) {
      const value = unwrap(settings, key)
      if (value != null && value !== '') values[key] = value
    }
    const brand = readBrand(values)
    paint(brand)
    /* Keep `/app`'s copy honest, so the next cold start is instant and the two
       programs do not disagree about what colour the product is. */
    writeLocalBrand(uid, writeBrand(brand))
    return brand
  } catch (err) {
    console.error('brand sync failed, keeping the cached brand', err)
    return cached
  }
}

/**
 * Save a brand and make it stick everywhere.
 *
 * Paint first, then persist: the advisor asked for a visual change, and making
 * them wait on a network write to see it is the wrong trade. A failed write
 * says so rather than silently reverting.
 *
 * @param {string} uid
 * @param {import('./brand.js').Brand} brand
 * @returns {Promise<{ok: boolean, brand: import('./brand.js').Brand}>}
 */
export async function saveBrand(uid, brand) {
  const clean = readBrand(writeBrand(brand))
  paint(clean)
  writeLocalBrand(uid, writeBrand(clean))

  try {
    const at = new Date().toISOString()
    /** @type {Record<string, any>} */
    const patch = {}
    const values = writeBrand(clean)
    for (const key of Object.values(BRAND_KEYS)) {
      /* `wrap` produces `{v, at}` — the shape `/app` writes and the shape its
         last-write-wins comparison expects. A bare string is treated by its
         `hydrateSettingsFromCloud` as a value with no timestamp, which means
         "cloud wins" unconditionally. */
      patch[key] = wrap(values[key] || '', at)
    }
    await mergeDoc(['users', uid, 'settings', 'main'], patch)
    return { ok: true, brand: clean }
  } catch (err) {
    console.error('brand save failed; applied locally but will not follow you', err)
    return { ok: false, brand: clean }
  }
}
